// ui/states.jsx — 로딩 · 빈 상태 · 실패를 그리는 **유일한** 자리.
//
// 이 파일이 존재하는 이유는 QA v2 의 D-4 다: 권한 없는 Space 에서 셸을 그대로 그리고
// 「이 Space를 읽는 중」 같은 **거짓 문장**과 raw 오류 코드를 나란히 보여 줬다.
//
// 그 일이 가능했던 것은 화면이 "일단 그리고 실패하면 고친다"를 할 수 있었기 때문이다.
// `<Resource>` 는 그 선택지를 없앤다 — `ready` 가 아니면 children 은 **호출조차 되지 않는다.**

import { STATUS } from '../api/resource.js';
import { describeError, NEXT, SCOPE } from '../api/errors.js';
import { login } from '../api/client.js';
import { Button, Notice } from './primitives.jsx';
import './states.css';

/**
 * 로딩. **자리를 유지한다** — 스켈레톤이 레이아웃을 바꾸지 않는다(ux §5 공통 규칙 4).
 * 스피너를 쓰지 않는다: 회전하는 것은 재촉의 인상을 준다.
 */
export function Loading({ label = '읽는 중' }) {
  return (
    <div class="state state--loading" role="status">
      <span class="state__bar" aria-hidden="true" />
      <span class="t-meta">{label}</span>
    </div>
  );
}

/**
 * 빈 상태. 높이 ≤160px, **좌측 정렬**(§4.4). 가운데 정렬한 거대한 빈 화면을 만들지 않는다.
 */
export function Empty({ title, children, action }) {
  return (
    <div class="state state--empty">
      <p class="t-body-strong">{title}</p>
      {children ? <p class="t-meta state__desc">{children}</p> : null}
      {action ? <div class="state__action">{action}</div> : null}
    </div>
  );
}

/**
 * 실패. **문장은 상태 사전에서만 온다.** 화면이 문장을 지어내지 않고, raw code 는
 * 접힌 자리에만 둔다(D-6: raw 이벤트/오류 코드를 사람 문장 자리에 쓰지 않는다).
 *
 * @param onRetry  없으면 재시도 버튼을 **그리지 않는다**. 무동작 버튼을 만들지 않는다(D-7).
 */
export function ErrorView({ error, onRetry, onJoin, onSelectNode, compact = false }) {
  const d = describeError(error);

  return (
    <div class={`state state--error${compact ? ' state--compact' : ''}`}>
      <Notice
        tone={d.scope === SCOPE.SESSION || d.scope === SCOPE.WIRING ? 'danger' : 'waiting'}
        title={d.message}
        action={<ErrorActions d={d} onRetry={onRetry} onJoin={onJoin} onSelectNode={onSelectNode} />}
      >
        {d.admin ? <p class="t-meta">배포 설정 문제일 수 있습니다. 운영자에게 알려 주세요.</p> : null}
        {d.technical ? (
          <details class="state__tech">
            <summary class="t-meta">기술 정보</summary>
            <code class="t-mono t-meta">{d.technical}</code>
          </details>
        ) : null}
      </Notice>
    </div>
  );
}

function ErrorActions({ d, onRetry, onJoin, onSelectNode }) {
  switch (d.next) {
    case NEXT.RELOGIN:
      return (
        <Button size="sm" tone="human" onClick={() => login()}>
          다시 로그인
        </Button>
      );
    case NEXT.ONBOARD:
      return (
        <Button size="sm" tone="human" onClick={() => window.location.assign('/onboarding')}>
          사람 Node 만들기
        </Button>
      );
    case NEXT.SELECT_NODE:
      return onSelectNode ? (
        <Button size="sm" onClick={onSelectNode}>
          Node 고르기
        </Button>
      ) : null;
    case NEXT.JOIN:
      return onJoin ? (
        <Button size="sm" onClick={onJoin}>
          참여 신청
        </Button>
      ) : null;
    case NEXT.REREAD:
      return onRetry ? (
        <Button size="sm" onClick={onRetry}>
          다시 읽기
        </Button>
      ) : null;
    case NEXT.RETRY:
    case NEXT.RETRY_ONCE:
      return onRetry ? (
        <Button size="sm" onClick={onRetry}>
          다시 시도
        </Button>
      ) : null;
    case NEXT.NONE:
    default:
      // 사람이 할 수 있는 일이 없으면 버튼을 그리지 않는다.
      return null;
  }
}

/**
 * 읽기 하나(또는 `combine()` 결과)를 화면에 붙이는 게이트.
 *
 * `children` 은 **함수**다. `ready` 일 때만 호출되므로, 실패·로딩 중에 data 를 만지는 코드가
 * 존재할 수 없다. 이것이 D-4 를 구조적으로 막는 방식이다.
 *
 * @param {{ status, data, error, reload }} resource
 * @param {(data:any)=>any} children
 */
export function Resource({ resource, children, loadingLabel, onJoin, onSelectNode, compact }) {
  if (resource.status === STATUS.ERROR) {
    return (
      <ErrorView
        error={resource.error}
        onRetry={resource.reload}
        onJoin={onJoin}
        onSelectNode={onSelectNode}
        compact={compact}
      />
    );
  }
  if (resource.status === STATUS.LOADING) {
    return <Loading label={loadingLabel} />;
  }
  return children(resource.data);
}
